import React, { useState, useCallback, useEffect } from 'react';
import { IconButton, Tooltip, Dialog, DialogTitle, DialogContent, IconButton as CloseButton, CircularProgress, Box, Typography, Checkbox, FormControlLabel } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import ReactMarkdown from 'react-markdown';
import FlexService from '../../services/FlexService';

const TTS_PREF_KEY = 'fast_help_tts_enabled';

// Strips markdown syntax down to plain, speakable prose -- reading "asterisk
// asterisk Warehouse Config asterisk asterisk" aloud would be unusable, and
// image/link syntax has no useful spoken form at all.
const stripMarkdownForSpeech = (md) => {
  return md
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*([^*]*)\*\*/g, '$1')
    .replace(/\*([^*]*)\*/g, '$1')
    .replace(/^[-*]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/-{2,}/g, '')
    .replace(/\n{2,}/g, '. ')
    .replace(/\n/g, ' ')
    .trim();
};

/**
 * HelpIcon - Small "?" affordance that opens a Dialog with contextual help
 * for the current workspace/view/panel. Content is fetched on demand (not
 * preloaded) by running the `get_view_help` FLEX Command with the given viewKey --
 * that script tries the knowledgebase first, falling back to a curated
 * default per viewKey, then a generic message if nothing is registered.
 *
 * viewKey identifies what's being explained -- e.g. "WORKSPACECODE.viewId"
 * so two workspaces reusing the same viewId don't collide. Callers own the
 * exact convention; this component just passes it through. Callers should
 * pass a `key` that changes with viewKey so this remounts per page instead
 * of reusing stale fetched content across tab switches.
 *
 * get_view_help returns { content: <markdown string>, videoUrl?: <string> }.
 * videoUrl, when present, is rendered as an embedded player above the text --
 * e.g. a recorded walkthrough for the page, matching the wms-user-guide docs.
 *
 * Read-aloud uses the browser's built-in Web Speech API (no backend call) --
 * the "read automatically" preference persists in localStorage, off by
 * default, and applies across every HelpIcon instance in the app.
 */
const HelpIcon = ({ viewKey, title, colors, size = 'small' }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(() => localStorage.getItem(TTS_PREF_KEY) === 'true');

  const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const stopSpeaking = useCallback(() => {
    if (ttsSupported) window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [ttsSupported]);

  const speak = useCallback((markdown) => {
    if (!ttsSupported || !markdown) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(stripMarkdownForSpeech(markdown));
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utterance);
  }, [ttsSupported]);

  const handleOpen = useCallback(async () => {
    setOpen(true);
    if (content || loading) return;
    setLoading(true);
    setError(null);
    try {
      const response = await FlexService.executeScript({
        scriptName: 'get_view_help',
        context: { _payload: { viewKey } },
      });
      const ctx = response?.context || {};
      const result = ctx.result;
      const markdown = (result && typeof result === 'object') ? result.content : result;
      const finalContent = typeof markdown === 'string' && markdown.trim() ? markdown : 'No help content is available for this page yet.';
      setContent(finalContent);
      setVideoUrl((result && typeof result === 'object' && result.videoUrl) || null);
      if (autoSpeak) speak(finalContent);
    } catch (err) {
      // never show a raw "HTTP error 404" to the user (FH-187)
      setError("Help for this page couldn't be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [viewKey, content, loading, autoSpeak, speak]);

  const handleClose = () => {
    stopSpeaking();
    setOpen(false);
  };

  const handleAutoSpeakToggle = (e) => {
    const enabled = e.target.checked;
    setAutoSpeak(enabled);
    localStorage.setItem(TTS_PREF_KEY, enabled ? 'true' : 'false');
    if (!enabled) stopSpeaking();
  };

  // Stop any speech in flight if the component unmounts (tab switch, etc.)
  // while the dialog was open.
  useEffect(() => () => { if (ttsSupported) window.speechSynthesis.cancel(); }, [ttsSupported]);

  return (
    <>
      <Tooltip title="Help">
        <IconButton size={size} onClick={handleOpen} sx={{ color: colors?.textMuted, '&:hover': { color: '#22c55e' } }}>
          <HelpOutlineIcon fontSize={size} />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {title || 'Help'}
          <CloseButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </CloseButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} sx={{ color: '#22c55e' }} />
            </Box>
          )}
          {!loading && error && (
            <Typography variant="body2" color="error">{error}</Typography>
          )}
          {!loading && !error && (content || ttsSupported) && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={<Checkbox size="small" checked={autoSpeak} onChange={handleAutoSpeakToggle} disabled={!ttsSupported} />}
                label={<Typography variant="caption" sx={{ color: colors?.textSecondary }}>Read aloud automatically</Typography>}
              />
              {ttsSupported && content && (
                <Tooltip title={speaking ? 'Stop reading' : 'Read aloud'}>
                  <IconButton size="small" onClick={() => (speaking ? stopSpeaking() : speak(content))} sx={{ color: colors?.textMuted, '&:hover': { color: '#22c55e' } }}>
                    {speaking ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          {!loading && !error && videoUrl && (
            <Box sx={{ mb: 2 }}>
              <video controls style={{ width: '100%', borderRadius: 8 }}>
                <source src={videoUrl} type="video/mp4" />
              </video>
            </Box>
          )}
          {!loading && !error && content && (
            <Box sx={{ '& p': { mt: 0 }, '& h1, & h2, & h3': { mt: 2, mb: 1 } }}>
              <ReactMarkdown>{content}</ReactMarkdown>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HelpIcon;
